//
// Copyright (c) 2011-2025 by Curalate, Inc.
//

import { useInfiniteQuery } from '@tanstack/react-query';
import { useSelector } from 'react-redux';

import { getContentLibraryItems } from '@apis/content-library/contentLibraryApi';
import {
  IContentLibraryItemParams,
  IContentLibraryItemsResponse,
} from '@apis/content-library/contentLibraryApiModel';
import { getIsContentLibraryCampaignFiltersEnabled } from '@media-library/redux/getMediaLibraryPage';
import { QueryKeyConstants } from '@queries/QueryKeyConstants';
import { TWENTY_SECONDS_STALE_TIME } from '@queries/QueryOptionsConstants';
import { ErrorType, getErrorMessage } from '@utils/errorMessages';

export interface ContentItemsQueryParams {
  query: IContentLibraryItemParams;
}

export const useContentLibraryItemsInfiniteQuery = ({
  query,
}: ContentItemsQueryParams) => {
  const limit = query.limit ?? 12;

  // TODO: PD-299277 - Remove content_library_campaign_filters feature flag
  const isCampaignsFilterEnabled = useSelector(
    getIsContentLibraryCampaignFiltersEnabled,
  );

  const fetchContentLibraryItems = async (page = 0, signal?: AbortSignal) => {
    try {
      return await getContentLibraryItems(
        {
          ...query,
          filters: {
            ...query.filters,
            // Include targeted retailer campaign items if campaign filter is released
            ...(isCampaignsFilterEnabled
              ? {}
              : {
                  excludeTargetedRetailerCampaignItems: true,
                }),
          },
          limit,
          offset: page > 0 ? (page - 1) * limit : 0,
        },
        { signal },
      );
    } catch (error) {
      throw new Error(getErrorMessage(ErrorType.MEDIA_ITEMS_FETCH, error));
    }
  };

  return useInfiniteQuery<IContentLibraryItemsResponse, Error>({
    queryKey: [
      QueryKeyConstants.MediaFilters,
      QueryKeyConstants.MediaFiltersMediaItems,
      query,
    ],
    queryFn: async ({ pageParam, signal }) =>
      await fetchContentLibraryItems(pageParam, signal),
    getNextPageParam: (last, pages) =>
      last.contentItems.length < limit && last.numAvailable === 0
        ? undefined
        : pages.length + 1,
    staleTime: TWENTY_SECONDS_STALE_TIME,
    keepPreviousData: true,
  });
};
